import { Footer, LanguageSelector, LoadingSpinner } from '@web_opxp/components';
import { AuthSelectors, CookieConsent, FirebaseActions, getLegalComponent } from '@web_opxp/features';
import { ELanguage, LanguageActions, useDocumentLang, useTranslation } from '@web_opxp/language';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Outlet, useLocation } from 'react-router';
import { HistoryService } from 'web_core_library';
import * as AppSelectors from './selectors';
import { ONBOARDING_AVAILABLE_LANGUAGES } from '../base/constants';
import { AssetsBaseUrl, TrainingBaseUrl } from '../base/env';
import { getFooterMenu, getFooterSocialItems, getLegalLinks } from '../base/footer';
import useScrollToTop from '../base/scrollToTop';
import { EBlogUrls, getPrivacyPolicyUrl } from '../base/utils';
import HeaderWrapper from '../components/header/HeaderWrapper';
import { MedPatientsBanner, RecruitmentBanner } from '../recruitmentBanner';
import { EAppRoutes, isAssessmentPage, isPostRegPage } from '../routes';
import { isAppLoading, isFocusedMode } from './selectors';
import { AppActions } from './slice';

export interface IAppProps {
  loading: boolean;
  focused: boolean;
  isAppReady: boolean;
  session?: string;
  startApp: () => void;
  onLanguageSelect: (language: ELanguage) => void;
  onToggleCookieSettings: () => void;
}

export const AppView: React.FC<IAppProps> = ({
  loading,
  focused,
  isAppReady,
  session,
  startApp,
  onLanguageSelect,
  onToggleCookieSettings,
}) => {
  useEffect(() => {
    if (isAppReady) {
      return;
    }
    startApp();
  }, [isAppReady]);

  const { language, t } = useTranslation();
  useScrollToTop();

  useEffect(() => {
    if (loading) return;
    document.title = t('prereg:Welcome.Metadata.PageTitle');
    const descMeta = document.head.querySelector('meta[name="description"]');
    if (descMeta) {
      descMeta.setAttribute('content', t('prereg:Welcome.Metadata.Description'));
    }
  }, [loading, t]);
  const location = useLocation();
  const isPostReg = isPostRegPage(location.pathname);
  const runningSession = isAssessmentPage(location.pathname);
  useDocumentLang();
  const privacyUrl = getPrivacyPolicyUrl();

  if (loading) {
    return <LoadingSpinner assetsUrl={AssetsBaseUrl} />;
  }
  const languageSelect = (
    <LanguageSelector
      availableLanguages={ONBOARDING_AVAILABLE_LANGUAGES}
      current={language}
      onSelect={(lang) => onLanguageSelect(lang as ELanguage)}
    />
  );
  const [footerMenuItems, extraMenuItems] = getFooterMenu(session);

  const welcomePage = EAppRoutes.Welcome;
  const accountAction = session
    ? () => HistoryService.redirectLogout(`/${welcomePage}`)
    : () => HistoryService.redirectLogin(TrainingBaseUrl);

  return (
    <div className="App">
      <MedPatientsBanner />
      <RecruitmentBanner />
      <HeaderWrapper
        focused={focused}
        session={session}
        blogUrl={EBlogUrls.Effectiveness}
        accountAction={accountAction}
      />
      <div className="App-body">
        <Outlet />
      </div>
      <CookieConsent privacyUrl={privacyUrl} />
      <Footer
        languageSelector={!runningSession ? languageSelect : null}
        copyrightLine={t('common.common:Footer.Copyright') + ' | ' + t('common.common:Footer.ContactLine')}
        footerMenuItems={!isPostReg ? footerMenuItems : undefined}
        extraMenuItems={extraMenuItems}
        socialBarItems={!isPostReg ? getFooterSocialItems() : undefined}
        legalLinks={getLegalLinks()}
        onToggleCookieSettings={onToggleCookieSettings}
        cookieLinkTitle={t('common.common:Cookies.FooterLink') ?? ''}
        getLegalComponent={getLegalComponent}
      />
    </div>
  );
};

export const mapStateToProps = (state: any) => {
  const loading = isAppLoading(state);
  const focused = isFocusedMode(state);
  const session = AuthSelectors.getToken(state);
  const isAppReady = AppSelectors.isAppReady(state);
  return {
    loading,
    focused,
    session,
    isAppReady,
  };
};

export const mapDispatchToProps = {
  onLanguageSelect: LanguageActions.changeLanguage,
  onToggleCookieSettings: FirebaseActions.toggleSettings,
  startApp: AppActions.appStart,
};

const App = connect(mapStateToProps, mapDispatchToProps)(AppView);
export default App;
